import {
  HANDOFF_PARAMETER, MAX_HANDOFF_URL_LENGTH, OWNED_HOSTS, PARAMETERS, isAcquisitionPage,
} from "./acquisitionAttribution.js";
import { boundJourney, markIncomplete } from "./attributionJourney.js";

export function canCarryAttribution(url, location) {
  if (url.username || url.password || !isAcquisitionPage(url.pathname)) return false;
  const sameOrigin = url.origin === location.origin;
  return (sameOrigin && ["http:", "https:"].includes(url.protocol)) ||
    (url.protocol === "https:" && OWNED_HOSTS.includes(url.hostname) && !url.port);
}

export function attributionDestination(href, record, location) {
  try {
    const target = new URL(href, location.href);
    if (!canCarryAttribution(target, location) || !record) return href;
    target.searchParams.delete(HANDOFF_PARAMETER);
    const transport = new URL(target);
    let handoff = record;
    if (record.version === 2) {
      handoff = JSON.parse(JSON.stringify(record));
      const fits = value => {
        transport.searchParams.set(HANDOFF_PARAMETER, JSON.stringify(value));
        return transport.href.length <= MAX_HANDOFF_URL_LENGTH;
      };
      if (!boundJourney(handoff, fits, "handoff_limit")) {
        // Only summaries remain. Remove optional long fields before abandoning transport.
        markIncomplete(handoff, "handoff_limit");
        for (const field of ["landing_path", "term", "content", "campaign_id", "campaign", "referrer_host"]) {
          if (handoff.first_touch) delete handoff.first_touch[field];
          if (handoff.signup_touch) delete handoff.signup_touch[field];
          if (fits(handoff)) break;
        }
      }
    }
    transport.searchParams.set(HANDOFF_PARAMETER, JSON.stringify(handoff));
    if (transport.href.length <= MAX_HANDOFF_URL_LENGTH) return transport.href;
    if (record.version === 2) return target.href;
    // Preserve explicit destination parameters. Fall back to only bounded campaign labels.
    const touch = record.signup_touch || record.first_touch;
    for (const [parameter, field] of Object.entries(PARAMETERS)) {
      if (!touch?.[field] || target.searchParams.has(parameter)) continue;
      const fallback = new URL(target);
      fallback.searchParams.set(parameter, touch[field]);
      if (fallback.href.length <= MAX_HANDOFF_URL_LENGTH) target.href = fallback.href;
    }
    return target.href;
  } catch { return href; }
}

export function removeAttributionParameter(window) {
  try {
    const url = new URL(window.location.href);
    if (!url.searchParams.has(HANDOFF_PARAMETER)) return;
    url.searchParams.delete(HANDOFF_PARAMETER);
    window.history.replaceState(window.history.state, "", url.href);
  } catch { /* History access is optional. */ }
}

export function createAttributionNavigation({ window, document, getAttribution }) {
  // Retain originals so withdrawal/cleanup removes only parameters we generated.
  const links = new Map();
  let observer;
  let disposed = false;
  function decorateLink(link, record = getAttribution()) {
    if (!link?.getAttribute || !link.hasAttribute("href")) return;
    const href = link.getAttribute("href");
    let saved = links.get(link);
    if (!saved || href !== saved.decorated) saved = { original: href, decorated: href };
    if (saved.original.startsWith("#")) return;
    const decorated = attributionDestination(saved.original, record, window.location);
    saved.decorated = decorated;
    if (decorated !== saved.original) links.set(link, saved);
    else links.delete(link);
    if (href !== decorated) link.setAttribute("href", decorated);
  }

  function decorate() {
    if (disposed) return;
    const record = getAttribution();
    for (const link of document.querySelectorAll("a[href], area[href]")) decorateLink(link, record);
    for (const link of links.keys()) if (!link.isConnected) links.delete(link);
  }

  function restore() {
    for (const [link, saved] of links) {
      if (link.getAttribute("href") === saved.decorated) link.setAttribute("href", saved.original);
    }
    links.clear();
  }

  function onInteraction(event) {
    decorateLink(event.target?.closest?.("a[href], area[href]"));
  }
  const events = ["pointerdown", "click", "auxclick", "contextmenu", "focusin", "keydown"];
  for (const event of events) document.addEventListener(event, onInteraction, true);
  document.addEventListener("DOMContentLoaded", decorate);
  try {
    observer = new window.MutationObserver((mutations) => {
      if (mutations.some((mutation) => mutation.type === "childList")) decorate();
      else for (const mutation of mutations) decorateLink(mutation.target);
    });
    observer.observe(document.documentElement, { childList: true, subtree: true, attributes: true, attributeFilter: ["href"] });
  } catch { /* Interaction handlers still cover dynamic links. */ }
  decorate();

  return {
    decorate,
    restore,
    destination: (href) => attributionDestination(href, getAttribution(), window.location),
    dispose() {
      disposed = true;
      observer?.disconnect();
      for (const event of events) document.removeEventListener(event, onInteraction, true);
      document.removeEventListener("DOMContentLoaded", decorate);
      restore();
    },
  };
}
